import { motion, AnimatePresence } from 'framer-motion';
import { useEffect, useState } from 'react';
import { toast } from 'react-toastify';

import './style.css';
import { postJson } from '@/lib/apiConfig';
import { userFacingError } from '@/utils/translateApiMessage';

// Types
interface ForgotPasswordModalProps {
  isOpen: boolean;
  onClose: () => void;
  /** Quando true, não fecha ao clicar fora nem com Escape (fluxo de login obrigatório). */
  mandatory?: boolean;
  onBackToLogin?: () => void;
}

type Step = 'email' | 'reset';

const ForgotPasswordModal = ({
  isOpen,
  onClose,
  mandatory = false,
  onBackToLogin,
}: ForgotPasswordModalProps) => {
  const [email, setEmail] = useState('');
  const [verificationCode, setVerificationCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [currentStep, setCurrentStep] = useState<Step>('email');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  const handleClose = () => {
    setEmail('');
    setVerificationCode('');
    setNewPassword('');
    setConfirmPassword('');
    setCurrentStep('email');
    setIsLoading(false);
    setError('');
    onClose();
  };

  /**
   * Envia email de recuperação de senha
   * @param email - Email do usuário
   * @throws Error se a requisição falhar
   */
  const sendPasswordResetEmail = async (email: string): Promise<void> => {
    await postJson('kgStOPqy', 'send-email', { email: email.trim() });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Limpa erros anteriores
    setError('');

    // Valida se o email foi preenchido
    if (!email.trim()) {
      setError('Por favor, digite seu email');
      return;
    }

    setIsLoading(true);

    try {
      // Chama a API para enviar o email de recuperação
      await sendPasswordResetEmail(email);
      setCurrentStep('reset');
      setError('');
    } catch (error) {
      setError(userFacingError(error, 'Erro ao enviar email'));
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Redefine a senha do usuário (inclui verificação do código)
   * @param email - Email do usuário
   * @param token - Código de verificação
   * @param newPassword - Nova senha
   * @throws Error se a requisição falhar
   */
  const resetPassword = async (
    email: string,
    token: string,
    newPassword: string
  ): Promise<void> => {
    await postJson('kgStOPqy', 'recovery-pass', {
      email: email.trim(),
      token: token.trim(),
      password: newPassword.trim(),
    });
  };

  // Event handlers
  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    setEmail(e.target.value);
  };

  const handleCodeChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    // Permite apenas números e limita a 6 dígitos
    const value = e.target.value.replace(/\D/g, '').slice(0, 6);
    setVerificationCode(value);
  };

  const handleNewPasswordChange = (
    e: React.ChangeEvent<HTMLInputElement>
  ): void => {
    setNewPassword(e.target.value);
  };

  const handleConfirmPasswordChange = (
    e: React.ChangeEvent<HTMLInputElement>
  ): void => {
    setConfirmPassword(e.target.value);
  };

  const handleResetSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Limpa erros anteriores
    setError('');

    // Validações
    if (!verificationCode.trim()) {
      setError('Por favor, digite o código de verificação');
      return;
    }

    if (verificationCode.trim().length < 4) {
      setError('O código deve ter pelo menos 4 dígitos');
      return;
    }

    if (!newPassword.trim()) {
      setError('Por favor, digite a nova senha');
      return;
    }

    if (newPassword.length < 6) {
      setError('A senha deve ter pelo menos 6 caracteres');
      return;
    }

    if (newPassword !== confirmPassword) {
      setError('As senhas não coincidem');
      return;
    }

    setIsLoading(true);

    try {
      // Redefine a senha via API (inclui verificação do código)
      await resetPassword(email, verificationCode, newPassword);

      // Sucesso - mostra toast e fecha o modal
      const successToastId = toast.success(
        'Senha redefinida com sucesso! Faça login com sua nova senha.',
        {
          position: 'top-right',
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
        }
      );

      // Fecha o modal após um delay
      setTimeout(() => {
        toast.dismiss(successToastId);
        handleClose();
      }, 3000);
    } catch (error) {
      setError(userFacingError(error, 'Erro ao redefinir senha'));
    } finally {
      setIsLoading(false);
    }
  };

  // Navigation handlers
  const handleBackToEmail = (): void => {
    setCurrentStep('email');
    setVerificationCode('');
    setNewPassword('');
    setConfirmPassword('');
    setError('');
  };

  useEffect(() => {
    if (!mandatory || !isOpen) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        e.stopPropagation();
      }
    };
    window.addEventListener('keydown', onKeyDown, true);
    return () => window.removeEventListener('keydown', onKeyDown, true);
  }, [mandatory, isOpen]);

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          className={`forgot-password-overlay${mandatory ? ' forgot-password-overlay--mandatory' : ''}`}
          onClick={undefined}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.2 }}
        >
          <motion.div
            className='forgot-password-container'
            onClick={e => e.stopPropagation()}
            initial={{ opacity: 0, y: -30, scale: 0.9 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -30, scale: 0.9 }}
            transition={{ duration: 0.3, ease: 'easeOut' }}
          >
            <motion.div
              className='forgot-password-header'
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.25, ease: 'easeOut', delay: 0.1 }}
            >
              <h2 className='forgot-password-title'>
                {currentStep === 'reset'
                  ? 'Redefinir Senha'
                  : 'Recuperar Senha'}
              </h2>
              <p className='forgot-password-subtitle'>
                {currentStep === 'reset'
                  ? `Digite o código enviado para ${email} e sua nova senha`
                  : 'Digite seu email para receber o código de verificação'}
              </p>
            </motion.div>

            {currentStep === 'email' ? (
              <motion.form
                onSubmit={handleSubmit}
                className='forgot-password-form'
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.25, ease: 'easeOut', delay: 0.2 }}
              >
                <motion.div
                  className='forgot-password-form-group'
                  initial={{ opacity: 0, y: 15 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.2, ease: 'easeOut', delay: 0.3 }}
                >
                  <label htmlFor='email' className='forgot-password-form-label'>
                    Email
                  </label>
                  <input
                    type='email'
                    id='email'
                    name='email'
                    value={email}
                    onChange={handleEmailChange}
                    className='forgot-password-form-input'
                    placeholder='seu@email.com'
                    required
                    autoFocus
                  />
                </motion.div>

                {error && (
                  <motion.div
                    className='forgot-password-error'
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.2 }}
                  >
                    {error}
                  </motion.div>
                )}

                <motion.div
                  className='forgot-password-form-actions'
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.25, ease: 'easeOut', delay: 0.4 }}
                >
                  <button
                    type='submit'
                    className='forgot-password-btn-primary'
                    disabled={isLoading}
                  >
                    {isLoading ? 'Enviando...' : 'Enviar Código'}
                  </button>
                </motion.div>
              </motion.form>
            ) : (
              <motion.form
                onSubmit={handleResetSubmit}
                className='forgot-password-form'
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.25, ease: 'easeOut', delay: 0.2 }}
              >
                <motion.div
                  className='forgot-password-form-group'
                  initial={{ opacity: 0, y: 15 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.2, ease: 'easeOut', delay: 0.3 }}
                >
                  <label
                    htmlFor='verification-code'
                    className='forgot-password-form-label'
                  >
                    Código de Verificação
                  </label>
                  <input
                    type='text'
                    id='verification-code'
                    name='verification-code'
                    value={verificationCode}
                    onChange={handleCodeChange}
                    className='forgot-password-form-input forgot-password-form-input--code'
                    placeholder='123456'
                    required
                    autoFocus
                    maxLength={6}
                    inputMode='numeric'
                    pattern='[0-9]*'
                  />
                </motion.div>

                <motion.div
                  className='forgot-password-form-group'
                  initial={{ opacity: 0, y: 15 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.2, ease: 'easeOut', delay: 0.35 }}
                >
                  <label
                    htmlFor='new-password'
                    className='forgot-password-form-label'
                  >
                    Nova Senha
                  </label>
                  <input
                    type='password'
                    id='new-password'
                    name='new-password'
                    value={newPassword}
                    onChange={handleNewPasswordChange}
                    className='forgot-password-form-input'
                    placeholder='Digite sua nova senha'
                    required
                    minLength={6}
                  />
                </motion.div>

                <motion.div
                  className='forgot-password-form-group'
                  initial={{ opacity: 0, y: 15 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.2, ease: 'easeOut', delay: 0.4 }}
                >
                  <label
                    htmlFor='confirm-password'
                    className='forgot-password-form-label'
                  >
                    Confirmar Nova Senha
                  </label>
                  <input
                    type='password'
                    id='confirm-password'
                    name='confirm-password'
                    value={confirmPassword}
                    onChange={handleConfirmPasswordChange}
                    className='forgot-password-form-input'
                    placeholder='Confirme sua nova senha'
                    required
                    minLength={6}
                  />
                </motion.div>

                {error && (
                  <motion.div
                    className='forgot-password-error'
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.2 }}
                  >
                    {error}
                  </motion.div>
                )}

                <motion.div
                  className='forgot-password-form-actions'
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.25, ease: 'easeOut', delay: 0.5 }}
                >
                  <button
                    type='submit'
                    className='forgot-password-btn-primary'
                    disabled={isLoading}
                  >
                    {isLoading ? 'Redefinindo...' : 'Redefinir Senha'}
                  </button>

                  <button
                    type='button'
                    onClick={handleBackToEmail}
                    className='forgot-password-btn-secondary'
                    disabled={isLoading}
                  >
                    Alterar Email
                  </button>
                </motion.div>
              </motion.form>
            )}

            <motion.div
              className='forgot-password-footer'
              initial={{ opacity: 0, y: 15 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.25, ease: 'easeOut', delay: 0.5 }}
            >
              <p className='forgot-password-back-text'>
                {currentStep === 'reset' ? (
                  <>
                    Não recebeu o código?{' '}
                    <button
                      type='button'
                      onClick={handleBackToEmail}
                      className='forgot-password-back-link'
                    >
                      Enviar novamente
                    </button>
                    {mandatory && onBackToLogin && (
                      <>
                        {' · '}
                        <button
                          type='button'
                          onClick={onBackToLogin}
                          className='forgot-password-back-link'
                        >
                          Voltar ao login
                        </button>
                      </>
                    )}
                  </>
                ) : (
                  <>
                    Lembrou da senha?{' '}
                    <button
                      type='button'
                      onClick={onBackToLogin}
                      className='forgot-password-back-link'
                    >
                      Voltar ao login
                    </button>
                  </>
                )}
              </p>
            </motion.div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default ForgotPasswordModal;
